import {
  TreasureClaimResponse,
  TreasurePoint,
  TreasurePointKind,
  TreasurePointsResponse,
  TreasureSettings,
  TreasureStarfallInfo,
} from "../types/treasure"

type RawRecord = Record<string, unknown>

const DEFAULT_RADIUS_M = 50
const DEFAULT_DURATION_MINUTES = 30
const DEFAULT_COIN_AMOUNT = 100

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function pickIsoDate(value: unknown): string | null {
  if (value == null || value === "") return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return null
    const date = new Date(trimmed)
    return Number.isNaN(date.getTime()) ? trimmed : date.toISOString()
  }
  return null
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function normalizeTreasureSettings(raw: unknown): TreasureSettings | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  return {
    windowStartHour:
      pickNumber(item.windowStartHour ?? item.window_start_hour, 7) ?? 7,
    windowEndHour:
      pickNumber(item.windowEndHour ?? item.window_end_hour, 20) ?? 20,
    activeCount: pickNumber(item.activeCount ?? item.active_count, 1) ?? 1,
    wavesPerDay: pickNumber(item.wavesPerDay ?? item.waves_per_day, 5) ?? 5,
    durationMinutes:
      pickNumber(
        item.durationMinutes ?? item.duration_minutes,
        DEFAULT_DURATION_MINUTES
      ) ?? DEFAULT_DURATION_MINUTES,
    coinAmount:
      pickNumber(item.coinAmount ?? item.coin_amount, DEFAULT_COIN_AMOUNT) ??
      DEFAULT_COIN_AMOUNT,
  }
}

function normalizeTreasureKind(value: unknown): TreasurePointKind {
  const raw = pickString(value).toLowerCase()
  return raw === "starfall" ? "starfall" : "treasure"
}

export function normalizeTreasurePoint(raw: unknown): TreasurePoint | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  const cityId = pickNumber(item.cityId ?? item.city_id)
  if (id == null || lat == null || lng == null || cityId == null) return null

  const kind = normalizeTreasureKind(item.kind)
  return {
    id: String(id),
    name: pickString(
      item.name ?? item.title,
      kind === "starfall" ? `Звездопад ${id}` : `Клад ${id}`
    ),
    latitude: lat,
    longitude: lng,
    radiusMeters:
      pickNumber(
        item.radiusMeters ?? item.radius_meters ?? item.radiusM ?? item.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
    cityId,
    kind,
    isActiveNow: pickBool(item.isActiveNow ?? item.is_active_now),
    activeUntil: pickIsoDate(item.activeUntil ?? item.active_until),
    activationId:
      pickString(item.activationId ?? item.activation_id) || null,
    nextStartsAt: pickIsoDate(item.nextStartsAt ?? item.next_starts_at),
    coinAmount: pickNumber(item.coinAmount ?? item.coin_amount),
    dustAmount: pickNumber(item.dustAmount ?? item.dust_amount),
  }
}

function normalizeStarfallInfo(raw: unknown): TreasureStarfallInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  return {
    dustAmount: pickNumber(item.dustAmount ?? item.dust_amount, 40) ?? 40,
    activeCount: pickNumber(item.activeCount ?? item.active_count, 4) ?? 4,
  }
}

export function normalizeTreasurePointsResponse(
  data: unknown
): TreasurePointsResponse {
  const obj = unwrapPayload(data)
  const settings = normalizeTreasureSettings(obj.settings)
  const listRaw = obj.points ?? obj.items
  const points = Array.isArray(listRaw)
    ? listRaw
        .map(normalizeTreasurePoint)
        .filter((point): point is TreasurePoint => point != null)
    : []

  const claimTodayCoins = pickNumber(
    obj.claimTodayCoins ?? obj.claim_today_coins
  )

  return {
    isEnabled: pickBool(obj.isEnabled ?? obj.is_enabled),
    claimedToday: pickBool(obj.claimedToday ?? obj.claimed_today),
    claimTodayCoins,
    settings,
    starfall: normalizeStarfallInfo(obj.starfall),
    points,
  }
}

export function normalizeTreasureClaimResponse(
  raw: unknown
): TreasureClaimResponse | null {
  const obj = unwrapPayload(raw)
  const pointId = pickString(obj.pointId ?? obj.point_id)
  const coins = pickNumber(obj.coins, 0) ?? 0
  const dust = pickNumber(obj.dust, 0) ?? 0
  const claimedAt = pickIsoDate(obj.claimedAt ?? obj.claimed_at)
  if (!pointId || !claimedAt) return null
  if (coins <= 0 && dust <= 0) return null

  const kind = normalizeTreasureKind(obj.kind)
  return {
    kind: dust > 0 ? "starfall" : kind,
    coins,
    dust,
    dustElement: pickString(obj.dustElement ?? obj.dust_element) || null,
    pointId,
    pointName: pickString(
      obj.pointName ?? obj.point_name,
      dust > 0 ? "Звездопад" : "Клад"
    ),
    claimedAt,
    claimedToday: pickBool(obj.claimedToday ?? obj.claimed_today, coins > 0),
  }
}
